export interface CustomFieldTicketScope {
  appliesToAllTickets?: boolean;
  ticketScopeIds?: string[];
}

export interface TicketScopeOption {
  id: string;
  name: string;
}

export interface CatalogTicketRef {
  _id: string;
  name: string;
  ticketTypeId?: string;
}

export function getCatalogTicketsForFieldScope(
  ticketCatalogMode: string | undefined,
  tickets: Array<{ _id?: string; name: string; isActive?: boolean }> | undefined,
  ticketTypes: Array<{ _id?: string; name: string; isActive?: boolean }> | undefined
): TicketScopeOption[] {
  if (ticketCatalogMode === 'phased' || ticketCatalogMode === 'batched') {
    return (ticketTypes || [])
      .filter((ticket) => ticket.isActive !== false)
      .map((ticket) => ({
        id: String(ticket._id || ticket.name),
        name: ticket.name,
      }));
  }

  return (tickets || [])
    .filter((ticket) => ticket.isActive !== false)
    .map((ticket) => ({
      id: String(ticket._id || ticket.name),
      name: ticket.name,
    }));
}

export function expandTicketScopeIds(
  selectedIds: string[],
  options: TicketScopeOption[]
): string[] {
  const expanded = new Set<string>();

  for (const rawId of selectedIds) {
    const id = String(rawId).trim();
    if (!id) continue;
    expanded.add(id);

    const option = options.find((entry) => entry.id === id || entry.name === id);
    if (option) {
      expanded.add(option.id);
      expanded.add(option.name);
    }
  }

  return Array.from(expanded);
}

export function normalizeCustomFieldScope(field: CustomFieldTicketScope): CustomFieldTicketScope {
  const ticketScopeIds = (field.ticketScopeIds || []).map((id) => String(id).trim()).filter(Boolean);
  const appliesToAllTickets = ticketScopeIds.length > 0 ? false : field.appliesToAllTickets !== false;
  return { ...field, ticketScopeIds, appliesToAllTickets };
}

function buildScopeMatchers(
  scopeIds: string[],
  catalogTickets?: CatalogTicketRef[]
): Set<string> {
  const matchers = new Set(scopeIds.map((id) => String(id).trim()).filter(Boolean));
  if (!catalogTickets?.length) return matchers;

  Array.from(matchers).forEach((scopedId) => {
    const catalogEntry = catalogTickets.find(
      (ticket) =>
        String(ticket._id) === scopedId ||
        ticket.name === scopedId ||
        (ticket.ticketTypeId && String(ticket.ticketTypeId) === scopedId)
    );
    if (!catalogEntry) return;
    matchers.add(String(catalogEntry._id));
    matchers.add(catalogEntry.name);
    if (catalogEntry.ticketTypeId) matchers.add(String(catalogEntry.ticketTypeId));
  });

  return matchers;
}

export function customFieldAppliesToTicket(
  fieldInput: CustomFieldTicketScope,
  ticketId: string,
  ticket?: { ticketTypeId?: string; name?: string } | null,
  catalogTickets?: CatalogTicketRef[]
): boolean {
  const field = normalizeCustomFieldScope(fieldInput);
  if (field.appliesToAllTickets !== false) return true;

  const scopeIds = field.ticketScopeIds || [];
  if (!scopeIds.length || !String(ticketId || '').trim()) return false;

  const matchers = buildScopeMatchers(scopeIds, catalogTickets);
  const candidates = new Set<string>();
  if (ticketId) candidates.add(String(ticketId));
  if (ticket?.ticketTypeId) candidates.add(String(ticket.ticketTypeId));
  if (ticket?.name) candidates.add(ticket.name.trim());

  return Array.from(candidates).some((candidate) => matchers.has(candidate));
}

export function getCustomFieldScopeLabel(
  field: CustomFieldTicketScope,
  ticketOptions: TicketScopeOption[]
): string {
  const normalized = normalizeCustomFieldScope(field);
  if (normalized.appliesToAllTickets !== false) return 'Todos os ingressos';

  const scopeIds = normalized.ticketScopeIds || [];
  if (!scopeIds.length) return 'Nenhum ingresso selecionado';

  const names = scopeIds
    .map((id) => ticketOptions.find((option) => option.id === id || option.name === id)?.name || id)
    .filter(Boolean);

  const uniqueNames = Array.from(new Set(names));
  if (!uniqueNames.length) return `${scopeIds.length} ingresso(s)`;
  if (uniqueNames.length === 1) return uniqueNames[0];
  return `${uniqueNames.length} ingressos`;
}

export function isScopeOptionSelected(
  ticketScopeIds: string[],
  option: TicketScopeOption
): boolean {
  const scope = ticketScopeIds.map((id) => String(id).trim());
  return scope.indexOf(option.id) >= 0 || scope.indexOf(option.name) >= 0;
}
